/**
 * bossPhase3Constants.ts
 * Phase 3 tunables for the Void Mothership — ram charge + homing
 * missile barrage.
 *
 * Extracted from `bossConstants.ts` to keep that module under the 300-
 * line file budget. Constants for Phase 1 (turret + drone spawn) and
 * Phase 2 (shield cycle) remain in `bossConstants.ts`; this file owns
 * the Phase 3 weapons only.
 *
 * `bossConstants.ts` re-exports every symbol from this module so
 * existing call sites continue to import from `./bossConstants`
 * without churn — new code is free to import directly from here.
 *
 * NO React imports, NO Skia imports.
 *
 * @module bossPhase3Constants
 */

// ── Phase 3: ram charge ────────────────────────────────────────────────

/**
 * Seconds between successive ram charges while the boss is in HOLD.
 * Greybox cadence — long enough that the player has plenty of time
 * between rams to learn the turret + shield patterns, short enough that
 * the ram doesn't feel like a once-per-fight gimmick.
 * Like every other HOLD-only timer, this only ticks while the boss is
 * in HOLD and the ram state-machine is `IDLE` — once a ram windup
 * starts, the cooldown is paused until the boss returns to HOLD anchor.
 */
export const VOID_MOTHERSHIP_RAM_COOLDOWN_S = 8.0;

/**
 * Initial delay (s) between reaching HOLD and the first ram. Strictly
 * longer than `..._SHIELD_RAMP_IN_S` so the player sees a full
 * shield/expose cycle (and a turret bolt + drone wave) before the ram
 * arrives — the ram is the *last* tell the player learns.
 */
export const VOID_MOTHERSHIP_RAM_RAMP_IN_S = 5.0;

/**
 * Telegraph (s) — boss is committed to the ram but still anchored at
 * its HOLD position. A future visual slice will flash / shake the boss
 * during this beat; the entity itself just holds station so the player
 * can read the windup. Capped well under 1 s so the windup doesn't
 * compete with the shield-window timing.
 */
export const VOID_MOTHERSHIP_RAM_TELEGRAPH_S = 0.5;

/**
 * Speed (px / s, world-space) at which the boss travels during the
 * CHARGE leftward leg AND the RETURN rightward leg. Strictly greater
 * than the camera's auto-scroll speed so the RETURN can always catch up
 * to the moving HOLD anchor, and substantially faster than the boss's
 * ENTRY drift (60) so the charge reads as a committed dash rather than
 * a second entry slide.
 */
export const VOID_MOTHERSHIP_RAM_SPEED = 320;

/**
 * Distance (px, world-space) the boss travels LEFT of its HOLD anchor
 * during the CHARGE leg before reversing into RETURN. Bounded well
 * below `VIEWPORT_WIDTH` so the boss never punches off-screen-left
 * even from the right edge of the hold (HOLD_X_OFFSET = 360, viewport
 * 480, so 200 leaves a 40 px safety margin on the left of the
 * viewport-relative HOLD position).
 */
export const VOID_MOTHERSHIP_RAM_DISTANCE = 200;

/**
 * Seconds between successive trail-burst spawns while the boss is in
 * the CHARGE leg. Total CHARGE duration ≈ 0.625 s
 * (`RAM_DISTANCE / RAM_SPEED = 200 / 320`); 0.12 s spacing yields ~5
 * bursts across the leg — readable as a continuous exhaust trail
 * without saturating the 16-slot explosion pool (turret bolts +
 * drones + missile hits may also be spawning bursts in the same tick).
 */
export const VOID_MOTHERSHIP_RAM_TRAIL_INTERVAL_S = 0.12;

// ── Phase 3: homing missile barrage ────────────────────────────────────

/**
 * Seconds between successive homing-missile launches while the boss is
 * in HOLD. Slower cadence than the turret bolt (2.0 s) because each
 * missile is a persistent threat that lingers until it overshoots or
 * times out — letting them stack faster would overload the screen.
 */
export const VOID_MOTHERSHIP_MISSILE_COOLDOWN_S = 4.0;

/**
 * Initial delay (s) between reaching HOLD and the first missile launch.
 * The longest ramp-in of any HOLD-phase weapon — the player should read
 * turret → drone → shield cycle → ram windup before missiles arrive,
 * so the fight escalates in legible beats rather than dumping every
 * threat on the same tick.
 */
export const VOID_MOTHERSHIP_MISSILE_RAMP_IN_S = 6.0;

/**
 * Forward speed (px/s) at which the missile travels along its current
 * heading. Slower than the turret bolt (`..._PROJECTILE_SPEED`) so the
 * player has time to read and juke the homing turn. Constant magnitude
 * — heading rotates over time but the linear speed never changes.
 */
export const VOID_MOTHERSHIP_MISSILE_SPEED = 200;

/**
 * Maximum angular turn rate (radians per second) of the missile's
 * heading toward the ship. Low enough that a steady evasive arc on the
 * ship's side can break lock; high enough that holding station means
 * the missile will eventually connect.
 */
export const VOID_MOTHERSHIP_MISSILE_TURN_RATE_RAD_S = 1.5;

/**
 * Time-to-live (s) for an in-flight missile. Bounds the live-missile
 * population in case the ship outruns the homing turn — without this
 * the missile would orbit indefinitely. Long enough that a straight
 * chase from spawn to viewport-left at `..._MISSILE_SPEED` (200 px/s)
 * across the full viewport (480 px) completes well within the window.
 */
export const VOID_MOTHERSHIP_MISSILE_TTL_S = 6.0;

/** Width (px, world-space) of a missile's AABB. */
export const VOID_MOTHERSHIP_MISSILE_WIDTH = 14;

/** Height (px, world-space) of a missile's AABB. */
export const VOID_MOTHERSHIP_MISSILE_HEIGHT = 8;

/**
 * Pool capacity for boss-fired missiles. Tuned well above the steady-
 * state in-flight count given `..._MISSILE_TTL_S / ..._MISSILE_COOLDOWN_S`
 * ≈ 1.5 concurrent missiles in steady state; 6 leaves headroom for
 * back-to-back launches after a long pause without exhausting the pool.
 */
export const VOID_MOTHERSHIP_MAX_BOSS_MISSILES = 6;

/**
 * Seconds between successive trail-burst spawns per in-flight boss
 * missile. Sized against the explosion pool budget: peak in-flight
 * count is bounded by `VOID_MOTHERSHIP_MAX_BOSS_MISSILES = 6`, each
 * burst lives `EXPLOSION_TOTAL_DURATION_S` (0.48 s), so a 0.25 s
 * interval yields ~2 active trails per missile → ~12 active bursts at
 * peak, comfortably under the 16-slot `EXPLOSION_POOL_SIZE` cap with
 * headroom left for other VFX sources (ram trail, asteroid death,
 * enemy death) running concurrently.
 */
export const VOID_MOTHERSHIP_MISSILE_TRAIL_INTERVAL_S = 0.25;
